import { useState } from "react";
import { motion } from "framer-motion";
import { BookOpen, ExternalLink } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import Reveal from "./Reveal";
import Emblem from "./Emblem";
import { useLang } from "@/i18n/LangContext";

const PRESALE_URL = "https://a.co/d/01oEWH9d";

const TEASER_TEXT = {
  es: { soon: "Ya a la venta", date: "Publicado el 6 de octubre", tagline: "Detrás de cada gobierno que cae, hay una historia que nadie cuenta. Javier Alagrande la escribe." },
  en: { soon: "Now on sale", date: "Published October 6", tagline: "Behind every fallen government, there is a story no one tells. Javier Alagrande writes it." },
  pt: { soon: "Já à venda", date: "Publicado em 6 de outubro", tagline: "Por trás de cada governo que cai, há uma história que ninguém conta. Javier Alagrande a escreve." },
  fr: { soon: "En vente", date: "Publié le 6 octobre", tagline: "Derrière chaque gouvernement qui tombe, il y a une histoire que personne ne raconte. Javier Alagrande l'écrit." },
};

const SAGA_TEXT = {
  es: [
    "Detrás de cada gobierno que cae, hay una historia que nadie cuenta. Javier Alagrande la escribe.",
    "La saga TODA LA VERDAD es una serie de novelas políticas ambientadas en el poder real de Venezuela y su entorno: los pasillos donde se negocian las lealtades, los cuarteles donde se decide lo que después se anuncia como historia oficial, y los hombres y mujeres que ejecutan o traicionan las órdenes que nunca llegan a los micrófonos.",
    "Cada libro de la colección es una obra de ficción inspirada en hechos y en el contexto político real de las últimas décadas en Venezuela. Los personajes centrales, sus nombres, sus conversaciones privadas y las tramas que protagonizan son producto de la imaginación del autor; el escenario histórico y político que las enmarca es el que el mundo ya conoce.",
    "TODA LA VERDAD, nada más que la verdad — Confesiones del guardaespaldas de Chávez abre la saga. Es el relato de un militar y médico cubano que caminó medio paso detrás de Fidel Castro y, después, de Hugo Chávez: el hombre que revisaba la comida, calculaba las rutas de escape y guardaba silencio incluso cuando empezó a entender lo que estaba presenciando. Catorce días que no figuran en ningún parte oficial, un desfalco que se movió con una firma digital mientras un país lloraba en la calle, y el precio exacto que tuvo, al final, el silencio de un hombre.",
    "Las siguientes entregas de la saga amplían ese mismo universo narrativo: el poder visto no desde los discursos, sino desde los pasillos, las órdenes que no se escriben y las decisiones que cambian el destino de millones de personas sin que nadie afuera se entere hasta mucho después.",
    "Lo que une a los libros de esta saga:",
    "• Personajes construidos a partir de un trabajo constante de investigación sobre el contexto venezolano",
    "• Tramas de espionaje, traición y supervivencia dentro de los círculos más cerrados del poder",
    "• Una mirada humana sobre quienes ejecutan el poder, no solo sobre quienes lo ostentan",
    "Javier Alagrande sigue investigando y escribiendo. Nuevas entregas de la saga TODA LA VERDAD, así como otros trabajos que nacen de esa misma investigación, están en camino.",
  ],
  en: [
    "Behind every fallen government, there is a story no one tells. Javier Alagrande writes it.",
    "The TODA LA VERDAD saga is a series of political novels set in Venezuela's real power and its surroundings: the hallways where loyalties are negotiated, the barracks where what is later announced as official history is decided, and the men and women who carry out —or betray— the orders that never reach the microphones.",
    "Each book in the collection is a work of fiction inspired by real events and by Venezuela's political context of recent decades. The central characters, their names, their private conversations and the plots they star in are products of the author's imagination; the historical and political stage that frames them is the one the world already knows.",
    "TODA LA VERDAD, nada más que la verdad — Confessions of Chávez's Bodyguard opens the saga. It is the account of a Cuban soldier and physician who walked half a step behind Fidel Castro and, later, Hugo Chávez: the man who checked the food, calculated escape routes and kept silent even as he began to understand what he was witnessing. Fourteen days that appear in no official report, an embezzlement moved with a digital signature while a country wept in the streets, and the exact price that one man's silence finally carried.",
    "The following installments expand that same narrative universe: power seen not from the speeches, but from the hallways, the unwritten orders and the decisions that change the fate of millions without anyone outside finding out until much later.",
    "What unites the books of this saga:",
    "• Characters built from constant research into the Venezuelan context",
    "• Plots of espionage, betrayal and survival inside the tightest circles of power",
    "• A humane gaze at those who execute power, not only those who hold it",
    "Javier Alagrande keeps investigating and writing. New installments of the TODA LA VERDAD saga, and other works born from that same research, are on their way.",
  ],
  pt: [
    "Por trás de cada governo que cai, há uma história que ninguém conta. Javier Alagrande a escreve.",
    "A saga TODA LA VERDAD é uma série de romances políticos ambientados no poder real da Venezuela e seu entorno: os corredores onde se negociam as lealdades, os quartéis onde se decide o que depois se anuncia como história oficial, e os homens e mulheres que executam ou traem as ordens que nunca chegam aos microfones.",
    "Cada livro da coleção é uma obra de ficção inspirada em fatos e no contexto político real das últimas décadas na Venezuela. Os personagens centrais, seus nomes, suas conversas privadas e as tramas que protagonizam são produto da imaginação do autor; o cenário histórico e político que as enquadra é o que o mundo já conhece.",
    "TODA LA VERDAD, nada mais que a verdade — Confissões do guarda-costas de Chávez abre a saga. É o relato de um militar e médico cubano que caminhou meio passo atrás de Fidel Castro e, depois, de Hugo Chávez: o homem que revisava a comida, calculava as rotas de fuga e guardava silêncio mesmo quando começou a entender o que estava presenciando. Catorze dias que não constam em nenhum relatório oficial, um desfalque movido com uma assinatura digital enquanto um país chorava nas ruas, e o preço exato que teve, no fim, o silêncio de um homem.",
    "As próximas entregas da saga ampliam esse mesmo universo narrativo: o poder visto não dos discursos, mas dos corredores, das ordens que não se escrevem e das decisões que mudam o destino de milhões de pessoas sem que ninguém lá fora saiba até muito depois.",
    "O que une os livros desta saga:",
    "• Personagens construídos a partir de um trabalho constante de pesquisa sobre o contexto venezuelano",
    "• Tramas de espionagem, traição e sobrevivência dentro dos círculos mais fechados do poder",
    "• Um olhar humano sobre quem executa o poder, não apenas sobre quem o ostenta",
    "Javier Alagrande segue investigando e escrevendo. Novas entregas da saga TODA LA VERDAD, assim como outros trabalhos que nascem dessa mesma pesquisa, estão a caminho.",
  ],
  fr: [
    "Derrière chaque gouvernement qui tombe, il y a une histoire que personne ne raconte. Javier Alagrande l'écrit.",
    "La saga TODA LA VERDAD est une série de romans politiques situés au cœur du pouvoir réel du Venezuela et de son environnement : les couloirs où se négocient les loyautés, les casernes où se décide ce qui sera ensuite annoncé comme l'histoire officielle, et les hommes et femmes qui exécutent — ou trahissent — les ordres qui n'arrivent jamais aux micros.",
    "Chaque livre de la collection est une œuvre de fiction inspirée de faits réels et du contexte politique vénézuélien des dernières décennies. Les personnages centraux, leurs noms, leurs conversations privées et les intrigues qu'ils incarnent sont le fruit de l'imagination de l'auteur ; le cadre historique et politique qui les entoure est celui que le monde connaît déjà.",
    "TODA LA VERDAD, nada más que la verdad — Confessions du garde du corps de Chávez ouvre la saga. C'est le récit d'un militaire et médecin cubain qui a marché un demi-pas derrière Fidel Castro puis Hugo Chávez : l'homme qui vérifiait la nourriture, calculait les itinéraires de fuite et gardait le silence même lorsqu'il a commencé à comprendre ce dont il était témoin. Quatorze jours qui ne figurent dans aucun rapport officiel, un détournement déplacé d'une signature numérique pendant qu'un pays pleurait dans la rue, et le prix exact qu'a finalement coûté le silence d'un homme.",
    "Les volets suivants de la saga élargissent ce même univers narratif : le pouvoir vu non pas depuis les discours, mais depuis les couloirs, les ordres qui ne s'écrivent pas et les décisions qui changent le destin de millions de personnes sans que personne à l'extérieur ne le sache avant longtemps.",
    "Ce qui unit les livres de cette saga :",
    "• Des personnages construits à partir d'un travail constant de recherche sur le contexte vénézuélien",
    "• Des intrigues d'espionnage, de trahison et de survie au sein des cercles les plus fermés du pouvoir",
    "• Un regard humain sur ceux qui exécutent le pouvoir, pas seulement sur ceux qui l'affichent",
    "Javier Alagrande continue d'enquêter et d'écrire. De nouveaux volets de la saga TODA LA VERDAD, ainsi que d'autres travaux nés de cette même recherche, sont en route.",
  ],
};

const SALE_TEXT = {
  es: "Ya a la venta en Amazon",
  en: "Now on sale on Amazon",
  pt: "Já à venda na Amazon",
  fr: "En vente sur Amazon",
};

const QR_BY_BOOK = {
  "libro-1": "/qr-toda-la-verdad.jpg",
  "libro-3": "/qr-la-bala.jpg",
};
const QR_TEASER = "/qr-3-de-enero.jpg";

const AmazonIcon = () => (
  <svg
    viewBox="0 0 24 24"
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3.5 15.5c5.2 4 11.8 4 17-1" />
    <path d="M17.6 12.4l3 1.8-2.2 2.9" />
  </svg>
);

const Books = () => {
  const { t, content, lang } = useLang();
  const TEASER = TEASER_TEXT[lang] ?? TEASER_TEXT.es;
  const TEASER_BOOK = {
    id: "libro-2-teaser",
    title: "Toda la Verdad: 3 de Enero",
    subtitle: null,
    year: null,
    fullSynopsis: SAGA_TEXT[lang] ?? SAGA_TEXT.es,
    amazonUrl: PRESALE_URL,
  };
  const BOOKS = content.books;
  const [fragment, setFragment] = useState(null);

  return (
    <section id="libros" data-testid="books-section" className="relative py-28 lg:py-36">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal className="mb-16 lg:mb-20 max-w-2xl">
          <p className="font-code text-xs tracking-[0.35em] uppercase text-[#C5A059] mb-4">
            {t("books.overline")}
          </p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#F4F1EA] tracking-tight">
            {t("books.headingA")} <span className="italic text-[#D4AF37]">{t("books.headingB")}</span>
          </h2>
          <p className="mt-5 text-[#A0A0A5] leading-relaxed">
            {t("books.intro")}
          </p>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-8 lg:gap-10">
          {BOOKS.map((book, i) => (
            <motion.article
              key={book.id}
              data-testid={`book-card-${book.id}`}
              initial={{ opacity: 0, y: 48 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] }}
              className={`group bg-[#141417] border border-[#F4F1EA]/10 hover:border-[#D4AF37]/40 transition-colors duration-500 flex flex-col ${i > 0 ? "md:order-3" : ""}`}
            >
              <div className="relative overflow-hidden aspect-[3/4]">
                <img
                  src={book.cover}
                  alt={`Carátula de ${book.title}`}
                  className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#141417] via-transparent to-transparent" />
                <span className="absolute top-4 left-4 font-code text-[11px] tracking-[0.3em] text-[#D4AF37] bg-[#0D0D0E]/80 backdrop-blur px-3 py-1.5 border border-[#D4AF37]/30">
                  {book.year || (book.amazonUrl ? TEASER.soon : t("books.reservedSub"))}
                </span>
              </div>

              <div className="p-7 flex flex-col flex-1">
                <h3 className="font-display text-2xl text-[#F4F1EA] leading-tight" data-testid={`book-title-${book.id}`}>
                  {book.title}
                </h3>
                {book.subtitle && (
                  <p className="mt-1.5 font-display italic text-lg text-[#C5A059]">{book.subtitle}</p>
                )}
                {book.id === "libro-3" && (
                  <p className="mt-2 font-code text-[11px] tracking-[0.25em] uppercase text-[#D4AF37]" data-testid="book-3-sale-date">
                    {SALE_TEXT[lang] ?? SALE_TEXT.es}
                  </p>
                )}
                <p className="mt-4 text-sm text-[#A0A0A5] leading-relaxed flex-1">{book.synopsis}</p>

                <div className="mt-7 flex flex-col gap-3">
                  {book.amazonUrl && (
                    <a
                      data-testid={`book-amazon-${book.id}`}
                      href={book.amazonUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2.5 px-5 py-3.5 bg-[#D4AF37] text-[#0D0D0E] text-xs tracking-[0.18em] uppercase font-semibold hover:bg-[#E5C158] transition-colors duration-300"
                    >
                      <AmazonIcon />
                      {t("books.amazon")}
                      <ExternalLink className="w-3 h-3 opacity-60" />
                    </a>
                  )}
                  {(book.fragment || book.fullSynopsis) && (
                    <button
                      data-testid={`book-fragment-${book.id}`}
                      onClick={() => setFragment(book)}
                      className="flex items-center justify-center gap-2.5 px-5 py-3.5 gold-hairline text-[#D4AF37] text-xs tracking-[0.18em] uppercase hover:bg-[#D4AF37]/10 transition-colors duration-300"
                    >
                      <BookOpen className="w-4 h-4" />
                      {book.fragment ? t("books.fragment") : t("books.synopsis")}
                    </button>
                  )}
                  {QR_BY_BOOK[book.id] && book.amazonUrl && (
                    <a
                      href={book.amazonUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`book-qr-${book.id}`}
                      className="mt-2 flex items-center gap-4 border border-[#F4F1EA]/10 bg-[#0D0D0E] p-3 hover:border-[#D4AF37]/40 transition-colors duration-300"
                    >
                      <img src={QR_BY_BOOK[book.id]} alt={`Código QR Amazon · ${book.title}`} className="w-20 h-20 rounded-sm" loading="lazy" />
                      <p className="font-code text-[10px] tracking-[0.22em] uppercase text-[#8E8E93] leading-relaxed">{t("books.scanQr")}</p>
                    </a>
                  )}
                </div>
              </div>
            </motion.article>
          ))}

          <motion.article
            data-testid="book-teaser-1"
            initial={{ opacity: 0, y: 48 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="group bg-[#141417] border border-[#D4AF37]/25 hover:border-[#D4AF37]/50 transition-colors duration-500 flex flex-col md:order-2"
          >
            <div className="relative overflow-hidden aspect-[3/4]">
              <img
                src="/teaser-libro-2.jpg"
                alt="Toda la Verdad · 3 de Enero — Pronto, por Javier Alagrande"
                className="w-full h-full object-cover object-top transition-transform duration-[1.2s] ease-out group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#141417] via-transparent to-transparent" />
              <span className="absolute top-4 right-4 font-code text-[11px] tracking-[0.3em] uppercase font-semibold bg-[#D4AF37] text-[#0D0D0E] px-3 py-1.5">
                {TEASER.soon}
              </span>
            </div>
            <div className="p-7 flex flex-col flex-1">
              <h3 className="font-display text-2xl text-[#F4F1EA] leading-tight" data-testid="teaser-title">
                Toda la Verdad
              </h3>
              <p className="mt-1.5 font-display italic text-lg text-[#C5A059]">{TEASER.date}</p>
              <p className="mt-4 text-sm text-[#A0A0A5] leading-relaxed flex-1">{TEASER.tagline}</p>
              <a
                data-testid="teaser-presale-cta"
                href={PRESALE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 flex items-center justify-center gap-2.5 px-5 py-3.5 bg-[#D4AF37] text-[#0D0D0E] text-xs tracking-[0.18em] uppercase font-semibold hover:bg-[#E5C158] transition-colors duration-300"
              >
                <AmazonIcon />
                {t("books.amazon")}
                <ExternalLink className="w-3 h-3 opacity-60" />
              </a>
              <button
                data-testid="teaser-saga-button"
                onClick={() => setFragment(TEASER_BOOK)}
                className="mt-3 flex items-center justify-center gap-2.5 px-5 py-3.5 gold-hairline text-[#D4AF37] text-xs tracking-[0.18em] uppercase hover:bg-[#D4AF37]/10 transition-colors duration-300"
              >
                <BookOpen className="w-4 h-4" />
                {t("books.synopsis")}
              </button>
              <a
                href={PRESALE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="teaser-qr"
                className="mt-5 flex items-center gap-4 border border-[#F4F1EA]/10 bg-[#0D0D0E] p-3 hover:border-[#D4AF37]/40 transition-colors duration-300"
              >
                <img src={QR_TEASER} alt="Código QR Amazon · Toda la Verdad: 3 de Enero" className="w-20 h-20 rounded-sm" loading="lazy" />
                <p className="font-code text-[10px] tracking-[0.22em] uppercase text-[#8E8E93] leading-relaxed">{t("books.scanQr")}</p>
              </a>
            </div>
          </motion.article>

        </div>
      </div>

      <Dialog open={!!fragment} onOpenChange={() => setFragment(null)}>
        <DialogContent
          data-testid="fragment-modal"
          className="bg-transparent border-none p-0 shadow-none max-w-4xl max-h-[90vh] overflow-y-auto [&>button]:text-[#2A2118] [&>button]:top-5 [&>button]:right-5"
        >
          {fragment && (
            <div>
              <DialogTitle className="sr-only">Fragmento de {fragment.title}</DialogTitle>
              <DialogDescription className="sr-only">
                Interior del libro {fragment.title} de Javier Alagrande
              </DialogDescription>
              <div className="relative grid md:grid-cols-2 rounded-sm overflow-hidden shadow-[0_50px_100px_rgba(0,0,0,0.65)]">
                <div className="hidden md:block absolute inset-y-0 left-1/2 -translate-x-1/2 w-12 bg-gradient-to-r from-transparent via-black/25 to-transparent pointer-events-none z-10" />

                {/* Página izquierda — portadilla interior */}
                <div className="bg-gradient-to-br from-[#EFE8D4] to-[#E2D7BA] text-[#2A2118] p-8 sm:p-12 flex flex-col justify-between min-h-[300px] md:min-h-[460px]">
                  <p className="font-code text-[10px] tracking-[0.4em] uppercase text-[#8A7322]">
                    {t("books.modalTag")}{fragment.year ? ` · ${fragment.year}` : ""}
                  </p>
                  <div className="py-8">
                    <h3 className="font-display text-3xl sm:text-4xl leading-tight" data-testid="fragment-book-title">
                      {fragment.title}
                    </h3>
                    <div className="mt-5 h-px w-16 bg-[#8A7322]/60" />
                    <p className="mt-5 font-display italic text-lg text-[#5C4A22]">Javier Alagrande</p>
                    <p className="mt-2 font-code text-[10px] tracking-[0.3em] uppercase text-[#8A7322]">
                      {t("books.edition")}
                    </p>
                  </div>
                  <p className="font-display italic text-sm text-[#8A7322]">iv</p>
                </div>

                {/* Página derecha — fragmento o sinopsis */}
                <div className="bg-gradient-to-br from-[#F6F0DE] to-[#EAE0C6] text-[#2A2118] p-8 sm:p-12 flex flex-col min-h-[300px] md:min-h-[460px]">
                  <p className="font-code text-[10px] tracking-[0.4em] uppercase text-[#8A7322] mb-6">
                    {fragment.fragment ? t("books.fragmentTag") : t("books.synopsisTag")}
                  </p>
                  <div className="flex-1 space-y-4" data-testid="fragment-text">
                    {(fragment.fullSynopsis ?? [fragment.fragment]).map((p, i, arr) => (
                      <p
                        key={i}
                        className={
                          fragment.id === "libro-1" && i === arr.length - 1
                            ? "text-xs italic leading-relaxed text-[#5C4A22] border-t border-[#8A7322]/30 pt-4"
                            : i === 0
                              ? "font-display text-lg leading-[1.8] text-justify first-letter:font-display first-letter:text-6xl first-letter:leading-[0.75] first-letter:float-left first-letter:mr-3 first-letter:mt-1.5 first-letter:text-[#8A7322]"
                              : "text-[15px] leading-[1.75] text-justify text-[#3A2F1F]"
                        }
                      >
                        {p}
                      </p>
                    ))}
                  </div>
                  <div className="mt-8 flex items-end justify-between">
                    <span className="font-code text-[10px] tracking-[0.3em] uppercase text-[#8A7322]">
                      {t("books.sign")}
                    </span>
                    <span className="font-display italic text-sm text-[#8A7322]">1</span>
                  </div>
                </div>
              </div>

              {fragment.amazonUrl && (
                <a
                  data-testid="fragment-amazon-cta"
                  href={fragment.amazonUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 flex items-center justify-center gap-2.5 px-5 py-3.5 bg-[#D4AF37] text-[#0D0D0E] text-xs tracking-[0.18em] uppercase font-semibold hover:bg-[#E5C158] transition-colors duration-300"
                >
                  <AmazonIcon />
                  {t("books.amazon")}
                  <ExternalLink className="w-3 h-3 opacity-60" />
                </a>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Books;
